import React, { useEffect, useState } from "react"
import axios from "axios"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"
import BoxesPerson from "url:assets/images/illustrations/BoxesPerson.png"
import BoxesDog from "url:assets/images/illustrations/BoxesDog.png"
import { Loader, LoaderTheme } from "components/atoms/Loader"
import { LenderIcon } from "../Status/LenderIcon"
import { MY_MOJO_API } from "../../../Config"
import { useGetDigitalRecommendationAccept } from "hooks/useAdminControls"
import { currencyFormat, ToCurrency } from "lib/CurrencyFormatter"
import { convertDecimalToYearsMonths } from "lib/Date"
import useRateCheck from "hooks/useRateCheck"
import { getClientConfig, MojoServiceTypes } from "lib/GetClientConfig"
import {
  AboutYourMortgageSection,
  ExtrasSection,
  KeyDetailsSection,
  MortgageDealPeriodSection,
  MortgageExpertInfo,
  RateCheckerSection,
  ReadyToAcceptSection,
  RecommendationDisclaimer,
  RecommendationDocumentsSection,
} from "./components"
import {
  AdditionalNotes,
  DogImage,
  EmptySubHeader,
  Header,
  HeaderContent,
  LoaderContainer,
  PersonImage,
  Root,
  SubHeader,
  SummaryContainer,
  SummaryDetails,
  SummaryDetailsContainer,
  SummaryDetailsDigits,
  SummaryDetailsTitle,
  SummaryIcon,
  SummaryImagesContainer,
  SummaryInfo,
  SummaryLendersContainer,
} from "./Recommendation.styles"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import { ApplicationDataProps, PreviewData } from "./types"
import { format, fromUnixTime } from "date-fns"
import { RecommendationContext, useRecommendationContext } from "./RecommendationContext"
import { DetailsSection } from "pages/Application/Recommendation/components/DetailsSection"
import { ReasonsForRecommendation } from "pages/Application/Recommendation/components/reasonsForRecommendation/ReasonsForRecommendation"
import { ApplicationSummaryEnhanced } from "models/ApplicationSummaryEnhanced"

export interface RecommendationWrapperProps {
  applicationId: string | null
  friendlyId: string | null
  serviceType: MojoServiceTypes
}

interface RecommendationProps {
  applicationData: ApplicationDataProps
  downloadEsisDocument: () => Promise<void> | void
  acceptRecommendationOpportunity?: () => Promise<void> | void
  acceptRecommendationQuote?: () => Promise<void> | void
  isDownloading: boolean
  isAccepting?: boolean
  isPreview?: boolean
  friendlyId?: string
  previewData?: PreviewData
}

export const customDimensions = (friendlyId: string, data: ApplicationDataProps) => ({
  friendlyId,
  lender_name: data.lenderName,
  product_name: data.productName,
  monthly_cost: data.initialMonthlyPayment?.toString(),
  initial_rate: data.initialRate?.toString(),
  mortgage_product_type: data.productType,
  initial_period_end_date: data.productEndDate,
  initial_fixed_period: data.initialFixedPeriod?.toString(),
  arrangement_fee: data.arrangementFee?.toString(),
  mortgage_payment_type: data.paymentType,
  offset: data.extras?.offset ? "Yes" : "No",
  portable: data.extras?.portable ? "Yes" : "No",
  overpayments_allowed: data.extras?.overpayments ? "Yes" : "No",
  free_legals: data.extras?.freeLegals ? "Yes" : "No",
  free_valuation: data.extras?.freeValuation ? "Yes" : "No",
  cashback: data.extras?.cashback?.toString(),
  full_page_url: `/application/${friendlyId}/recommendation`,
  productCondition: data.productCondition ?? "",
})

export const RecommendationWrapper = ({
  applicationId,
  friendlyId,
  serviceType,
}: RecommendationWrapperProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [applicationData, setApplicationData] = useState<ApplicationDataProps | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const isBtlSpecialist = serviceType === MojoServiceTypes.BTL_SPECIALIST

  useEffect(() => {
    if (applicationId) {
      axios
        .get(`${process.env.MY_MOJO_API}/recommendation/${applicationId}`, {
          withCredentials: true,
        })
        .then(({ data }) => {
          setApplicationData(data)
          setIsError(false)
          return setupScrollListener(loadCustomerFeedbackForm)
        })
        .catch((error) => {
          console.error("Recommendation not available:", error.message)
          setIsError(true)
          setIsLoading(false)
        })
    }
  }, [applicationId])

  useEffect(() => {
    if (isError) {
      LogGaEvent({
        action: EventAction.recommendationNotShown,
        event_category: EventCategory.siteInteraction,
        event_label: EventLabel.digiRecNotReady,
        friendlyId,
        full_page_url: `/application/${friendlyId}/recommendation`,
      })
    }
  }, [isError])

  useEffect(() => {
    if (applicationData) {
      LogGaEvent({
        action: EventAction.recommendationShown,
        event_category: EventCategory.siteInteraction,
        event_label: EventLabel.digiRec,
        ...customDimensions(friendlyId, applicationData),
      })
      setIsLoading(false)
    }
  }, [applicationData])

  const downloadEsisDocument = async (): Promise<void> => {
    const body = {
      s3Key: applicationData.esisS3Key,
      applicationId,
    }

    setIsDownloading(true)
    LogGaEvent({
      action: EventAction.documentDownload,
      event_category: EventCategory.functionalInteraction,
      event_label: EventLabel.digiRecEsis,
      ...customDimensions(friendlyId, applicationData),
    })
    try {
      const response = await axios.post(`${MY_MOJO_API}/documents/download-url`, body, {
        withCredentials: true,
      })
      if (response.status !== 200) {
        setIsDownloading(false)
        throw new Error("Error downloading Mortgage Illustration")
      }
      const url = response.data.downloadUrl
      setIsDownloading(false)
      window.location.assign(url)
    } catch (error) {
      console.error(error)
      setIsDownloading(false)
      alert("Error downloading Mortgage Illustration")
    }
  }

  const acceptRecommendationOpportunity = async () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.siteInteraction,
      event_label: EventLabel.digiRecAccepted,
      ...customDimensions(friendlyId, applicationData),
    })

    setIsAccepting(true)
    await axios
      .put(
        `${process.env.MY_MOJO_API}/recommendation/update-recommendation-accepted`,
        { applicationId, friendlyId },
        {
          withCredentials: true,
        }
      )
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setIsAccepting(false)
      })
  }

  const acceptRecommendationQuote = async () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.siteInteraction,
      event_label: EventLabel.digiRecAccepted,
      ...customDimensions(friendlyId, applicationData),
    })

    const quoteId = applicationData?.quoteId

    setIsAccepting(true)
    await axios
      .put(
        `${process.env.MY_MOJO_API}/recommendation/update-recommendation-quote-accepted`,
        { applicationId, quoteId },
        {
          withCredentials: true,
        }
      )
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setIsAccepting(false)
      })
  }

  const loadCustomerFeedbackForm = () => {
    const scriptTag: HTMLScriptElement = document.createElement("script")
    scriptTag.src = "//l.getsitecontrol.com/y79nm31w.js"
    scriptTag.type = "text/javascript"
    scriptTag.async = true
    document.body.appendChild(scriptTag)
    return () => {
      document.body.removeChild(scriptTag)
    }
  }

  const setupScrollListener = (callback: () => void) => {
    const handleScroll = () => {
      const scrollPosition: number = window.scrollY + window.innerHeight
      const totalHeight: number = document.documentElement.scrollHeight

      if (scrollPosition >= totalHeight / 2) {
        // Check if scrolled more than 50%
        callback()
        // Remove the event listener once the condition is met to prevent it from triggering multiple times
        window.removeEventListener("scroll", handleScroll)
      }
    }
    window.addEventListener("scroll", handleScroll)
    // Return a cleanup function to remove the event listener
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }

  return (
    <Root>
      {isLoading ? (
        <LoaderContainer>
          <Loader size={150} theme={LoaderTheme.Outline} />
        </LoaderContainer>
      ) : (
        <>
          {isError ? (
            <>
              <HeaderContent>
                <Header>Your recommendation isn’t ready just yet</Header>
                <EmptySubHeader>
                  <span>
                    Your Mortgage Expert will provide a recommendation after they have spoken to you
                    and have gained a better understanding of your situation and needs.
                  </span>
                  <span>You’ll be notified when your recommendation is ready.</span>
                </EmptySubHeader>
                <SummaryImagesContainer>
                  <PersonImage src={BoxesPerson} />
                  <DogImage src={BoxesDog} />
                </SummaryImagesContainer>
              </HeaderContent>
              <RecommendationDisclaimer />
            </>
          ) : (
            <RecommendationContext.Provider
              value={{
                serviceType,
                isBtlSpecialist,
                friendlyId,
              }}
            >
              <Recommendation
                serviceType={serviceType}
                applicationData={applicationData!}
                downloadEsisDocument={downloadEsisDocument}
                acceptRecommendationOpportunity={acceptRecommendationOpportunity}
                acceptRecommendationQuote={acceptRecommendationQuote}
                isDownloading={isDownloading}
                isPreview={false}
                isAccepting={isAccepting}
                friendlyId={friendlyId}
              />
            </RecommendationContext.Provider>
          )}
        </>
      )}
    </Root>
  )
}

interface IsPreviewReasonsForRecommendationProps {
  isPreview: boolean
  previewData?: PreviewData
  salesforceApplication: ApplicationSummaryEnhanced | null
  applicationData: ApplicationDataProps | null
}

const IsPreviewReasonsForRecommendation = ({
  isPreview,
  applicationData,
  previewData,
  salesforceApplication,
}: IsPreviewReasonsForRecommendationProps) =>
  isPreview ? (
    previewData && applicationData ? (
      <ReasonsForRecommendation
        debtConsolidation={previewData.debtConsolidation}
        mortgagePreferences={previewData.mortgagePreferences}
        caseComplexities={previewData.caseComplexities}
        productDetails={{
          arrangementFee: applicationData.arrangementFee,
          initialMonthlyPayment: applicationData.initialMonthlyPayment,
          reasonForRecommendation: applicationData?.reasonForRecommendation,
        }}
      />
    ) : null
  ) : salesforceApplication && applicationData ? (
    <ReasonsForRecommendation
      debtConsolidation={salesforceApplication.debtConsolidation}
      mortgagePreferences={salesforceApplication.mortgagePreferences}
      caseComplexities={salesforceApplication.caseComplexities}
      productDetails={{
        arrangementFee: applicationData.arrangementFee,
        initialMonthlyPayment: applicationData.initialMonthlyPayment,
        reasonForRecommendation: applicationData?.reasonForRecommendation,
      }}
    />
  ) : null

export const Recommendation = ({
  applicationData,
  downloadEsisDocument,
  acceptRecommendationOpportunity,
  acceptRecommendationQuote,
  isDownloading,
  isAccepting,
  isPreview = false,
  friendlyId,
  previewData,
}: RecommendationProps) => {
  const {
    leadApplicantFirstName,
    secondaryApplicantFirstName,
    lenderName,
    productName,
    initialMonthlyPayment,
    initialRate,
    productType,
    productEndDate,
    mortgageEndsBeforeProduct = false,
    mortgageExpertName,
    mortgageExpertImage,
    mortgageExpertPhone,
    mortgageExpertEmail,
    initialFixedPeriod,
    arrangementFee,
    quoteId,
    paymentType,
    previewEsisURL,
    accepted,
    acceptedVia,
    acceptedDateTime,
    earlyRepaymentChargesApply,
    earlyRepaymentCharge,
    followOnMonthlyRate,
    followOnMonthlyPayment,
    aprc,
    ltv = null,
    termYears = null,
    totalLoanAmount = null,
    currentMortgageBalance = null,
    additionalBorrowingAmount = null,
    propertyValue = null,
    purchasePrice = null,
    productTransfer,
    addArrangementFeeToLoan = false,
    additionalNotes,
    interestOnlyAmount,
    repaymentAmount,
    productCondition,
    extras: { offset, portable, overpayments, freeLegals, freeValuation, cashback },
    createdDate,
  } = applicationData
  const { isBtlSpecialist, serviceType } = useRecommendationContext()
  const formattedArrangementFee = currencyFormat(arrangementFee)
  const formattedMonthlyPayment = currencyFormat(initialMonthlyPayment)
  const formattedCashback = currencyFormat(cashback)
  const formattedFollowOnMonthlyPayment = currencyFormat(followOnMonthlyPayment)

  const { recommendationAcceptActive } = useGetDigitalRecommendationAccept(
    applicationData.applicationId
  )
  const [showRecommendationAccept, setShowRecommendationAccept] = useState(false)
  const [hasAccepted, setHasAccepted] = useState(false)
  const { application: salesforceApplication } = useApplicationSummaryEnhanced({
    friendlyId,
  })

  useEffect(() => {
    setShowRecommendationAccept(
      !!recommendationAcceptActive &&
        !!acceptRecommendationOpportunity &&
        !!acceptRecommendationQuote
    )
  }, [recommendationAcceptActive, acceptRecommendationOpportunity, acceptRecommendationQuote])

  const newFieldsNumber = [
    ltv,
    termYears,
    totalLoanAmount,
    currentMortgageBalance,
    additionalBorrowingAmount,
    propertyValue,
  ].filter((item) => item).length

  const isInRateCheckExperiment = useRateCheck(friendlyId)

  const { years, months } = convertDecimalToYearsMonths(termYears)

  const borrowingAmount = addArrangementFeeToLoan
    ? totalLoanAmount + arrangementFee
    : totalLoanAmount
  const borrowingAmountFormatted = ToCurrency(borrowingAmount)

  const { recommendation, fee } = getClientConfig(
    serviceType,
    salesforceApplication?.service?.status
  )

  const onAcceptRecommendation = () => {
    setHasAccepted(true)
    if (quoteId) {
      acceptRecommendationQuote()
    } else {
      acceptRecommendationOpportunity()
    }
  }

  const onUploadDocuments = () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: EventLabel.digiRecDocUpload,
      ...customDimensions(friendlyId, applicationData),
    })
  }

  return (
    <>
      <HeaderContent>
        <Header>
          Hey {leadApplicantFirstName && leadApplicantFirstName}
          {secondaryApplicantFirstName && ` & ${secondaryApplicantFirstName}`}, here’s your mortgage
          recommendation
        </Header>
        <SubHeader>
          This deal has been selected based on your needs and preferences as discussed with your
          Mortgage Expert at Mojo Mortgages.
        </SubHeader>
        {isBtlSpecialist && (
          <SubHeader>
            It is a dedicated buy-to-let deal and qualifies for Mojo Specialist.
          </SubHeader>
        )}
        {/*Don't bother showing the created date if it's a preview as it's not been created yet...*/}
        {!isPreview && (
          <SubHeader style={{ textWrap: "nowrap" }}>
            <b>Recommendation created: </b>
            {format(fromUnixTime(createdDate), "dd MMMM yyyy")}
          </SubHeader>
        )}
        <SummaryContainer>
          <PersonImage src={BoxesPerson} role="presentation" />
          <DogImage src={BoxesDog} role="presentation" />
          <SummaryLendersContainer>
            <SummaryIcon>
              <LenderIcon white={true} lenderName={lenderName} />
            </SummaryIcon>
            <SummaryInfo>{productName}</SummaryInfo>
          </SummaryLendersContainer>
          <SummaryDetailsContainer>
            <SummaryDetails>
              <SummaryDetailsTitle>Initial Rate</SummaryDetailsTitle>
              <SummaryDetailsDigits>{initialRate}%</SummaryDetailsDigits>
            </SummaryDetails>
            <SummaryDetails>
              <SummaryDetailsTitle>Monthly Payment</SummaryDetailsTitle>
              <SummaryDetailsDigits>£{formattedMonthlyPayment}</SummaryDetailsDigits>
            </SummaryDetails>
          </SummaryDetailsContainer>
        </SummaryContainer>
      </HeaderContent>
      <DetailsSection title="Key deal details">
        <KeyDetailsSection
          lenderName={lenderName}
          formattedMonthlyPayment={formattedMonthlyPayment}
          initialRate={initialRate}
          aprc={aprc}
          formattedArrangementFee={formattedArrangementFee}
          addArrangementFeeToLoan={addArrangementFeeToLoan}
          totalMonths={initialFixedPeriod}
          productType={productType}
          paymentType={paymentType}
          interestOnlyAmount={interestOnlyAmount}
          repaymentAmount={repaymentAmount}
          productCondition={productCondition}
          overpayments={overpayments}
          earlyRepaymentChargesApply={earlyRepaymentChargesApply}
          earlyRepaymentCharge={earlyRepaymentCharge}
          fee={fee}
        />
      </DetailsSection>
      <DetailsSection>
        <RateCheckerSection inRateCheckExperiment={isInRateCheckExperiment} />
      </DetailsSection>
      {(cashback || freeLegals || freeValuation || offset || portable) && (
        <DetailsSection title="Extras" smallTitle>
          <ExtrasSection
            offset={offset}
            portable={portable}
            cashback={cashback}
            formattedCashback={formattedCashback}
            freeLegals={freeLegals}
            freeValuation={freeValuation}
          />
        </DetailsSection>
      )}
      <DetailsSection title="When this deal ends">
        <MortgageDealPeriodSection
          mortgageEndsBeforeProduct={mortgageEndsBeforeProduct}
          productEndDate={productEndDate}
          followOnMonthlyRate={followOnMonthlyRate}
          formattedFollowOnMonthlyPayment={formattedFollowOnMonthlyPayment}
        />
      </DetailsSection>
      {newFieldsNumber > 0 && (
        <DetailsSection title="Your mortgage">
          <AboutYourMortgageSection
            propertyValue={propertyValue}
            purchasePrice={purchasePrice}
            currentMortgageBalance={currentMortgageBalance}
            additionalBorrowingAmount={additionalBorrowingAmount}
            totalLoanAmount={totalLoanAmount}
            borrowingAmountFormatted={borrowingAmountFormatted}
            addArrangementFeeToLoan={addArrangementFeeToLoan}
            formattedArrangementFee={formattedArrangementFee}
            ltv={ltv}
            termYears={termYears}
            years={years}
            months={months}
          />
        </DetailsSection>
      )}
      {additionalNotes && additionalNotes?.replace(/\s/g, "")?.length > 0 && (
        <DetailsSection title="Additional information">
          <AdditionalNotes>{additionalNotes}</AdditionalNotes>
        </DetailsSection>
      )}
      <IsPreviewReasonsForRecommendation
        isPreview={isPreview}
        previewData={previewData}
        salesforceApplication={salesforceApplication}
        applicationData={applicationData}
      />
      <DetailsSection title="Documents">
        <RecommendationDocumentsSection
          downloading={isDownloading}
          preview={isPreview}
          href={previewEsisURL}
          onClick={downloadEsisDocument}
        />
      </DetailsSection>
      {showRecommendationAccept && (
        <ReadyToAcceptSection
          productTransfer={productTransfer}
          recommendation={recommendation}
          lenderName={applicationData.lenderName}
          accepting={isAccepting}
          accepted={accepted}
          hasAccepted={hasAccepted}
          acceptedVia={acceptedVia}
          acceptedDateTime={acceptedDateTime}
          onAcceptRecommendation={onAcceptRecommendation}
          friendlyId={friendlyId}
          onUploadDocuments={onUploadDocuments}
        />
      )}
      <MortgageExpertInfo
        image={mortgageExpertImage}
        name={mortgageExpertName}
        phone={mortgageExpertPhone}
        email={mortgageExpertEmail}
      />
      <RecommendationDisclaimer freeLegals={freeLegals} />
    </>
  )
}
